import { Grid, Typography, useEventCallback } from '@mui/material';
import { Cropper } from 'shell/components/shared/cropper';
import { memo, useEffect, useMemo, useState } from 'react';
import { useFormik, useFormikContext } from 'formik';
import {
  CertificateType,
  CertificationFormVM,
  DocumentDeathFormVM,
} from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import DescriptionForm from 'civil-status/components/shared/description-document';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import DocumentDeathFormShared from 'civil-status/components/shared/death-form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { DocumentDeathCM } from 'shell/models/document-death';
import createSchema from '../../../../../constants/create-schema-death';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import {
  transformDocumentDeath,
  transformDocumentDeathRequestUpdate,
} from 'civil-status/utils/transform-document-death';
import { transformCertificateTitle } from 'civil-status/utils/transform-certificate-title';
import AnnotationForm from 'civil-status/components/shared/annotation';
import DocumentVisualizationsButtonsForm from './buttons';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { noop } from 'shell/utils/noop';

interface DocumentVisualizationDeathProps {
  onBack: () => void;
  onNext: (values: DocumentDeathFormVM) => void;
  type: string;
}

const DocumentVisualizationDeathComponent = ({
  onBack,
  onNext,
  type,
}: DocumentVisualizationDeathProps): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const { values: params } = formContext;

  const [death, getData] = useHttpFetch<HttpResponse<DocumentDeathCM>, DocumentDeathFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-morte-sima/visualizzazione-atto-morte/${params.chosenDocument?.actNumber}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentDeath,
    }
  );

  const updateDeath = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-morte-sima/modifica-atto-morte`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const formikForm = useFormik<DocumentDeathFormVM>({
    initialValues: death.data as DocumentDeathFormVM,
    enableReinitialize: true,
    validationSchema: createSchema,
    onSubmit: (values: DocumentDeathFormVM, context) => {
      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformDocumentDeathRequestUpdate(values);
        (
          updateDeath({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          unlockDocumentSubmit();
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          getData();
          context.setFieldValue('indicatorUpdate', '');
        });

        return;
      }

      unlockDocumentSubmit();
      onNext(values);
    },
  });

  const { values, errors, touched, setFieldValue: onChange, setValues: onChanges } = formikForm;

  const lockDocument = useHttpClient(`${__CIVIL_STATUS_API_URL__}/atti-morte-sima/blocca-atto`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // anno - registro - numero atto - parte - serie
    const arrActNumber = (params.chosenDocument?.actNumber || '').split('-');

    (
      lockDocument({
        data: {
          key: {
            anno: arrActNumber[0],
            numeroRegistro: arrActNumber[1],
            numeroAtto: arrActNumber[2],
            parte: arrActNumber[3],
            serie1: arrActNumber[4],
          },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      getData();
    });
  }, []);

  const unlockDocument = useHttpClient(`${__CIVIL_STATUS_API_URL__}/atti-morte-sima/sblocca-atto`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
    },
  });

  const unlockDocumentSubmit = useEventCallback(() => {
    unlockDocument({
      data: {
        key: {
          numeroAtto: values.documentNumber,
          numeroRegistro: values.registerNumber,
          parte: values.portion,
          serie1: `${values.serie || ''}${values.serie2 || ''}`,
          anno: values.year,
        },
      },
    });
  });

  const atto = useMemo(() => {
    return transformCertificateTitle({
      type: formContext.values?.chosenDocument?.element?.codiceAtto as CertificateType,
    });
  }, [formContext.values?.chosenDocument?.element?.codiceAtto]);

  return (
    <>
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Visualizzazione Atto {atto.type}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Error}
            title={!formikForm.isValid ? `Sono presenti errori` : ''}
            messages={
              !formikForm.isValid
                ? [
                    {
                      id: 1,
                      value: "Entrare in modifica dell'atto e compilare i campi obbligatori",
                    },
                  ]
                : []
            }
            visibility={!formikForm.isValid}
            onChangeVisibility={noop}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DocumentVisualizationsButtonsForm
            values={values}
            type={type}
            handleSubmit={() => formikForm.handleSubmit()}
            mode={mode}
            onBack={onBack}
            onChange={onChange}
            setMode={setMode}
          />
          <DescriptionForm values={values} />
          {mode === PageMode.Detail || mode === PageMode.Edit ? (
            <DocumentDeathFormShared
              onChange={onChange}
              values={values}
              errors={errors}
              mode={mode}
              touched={touched}
              onChanges={onChanges}
            />
          ) : undefined}
          <AnnotationForm
            values={values}
            onChange={onChange}
            mode={mode}
            type={type}
            setMode={setMode}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <StickyDiv>
            <Cropper
              images={values?.uploadFile?.raw || []}
              disabled={!values?.uploadFile?.raw?.length}
              onCut={(blob, indexImage) => {
                if (!blob) {
                  return;
                }
                blobToBase64(blob).then((base64) => {
                  onChange('uploadFile', {
                    ...(values?.uploadFile || {}),
                    raw:
                      values?.uploadFile?.raw?.map((el, index) =>
                        index === indexImage ? base64 : el
                      ) || [],
                    rawTiff: base64,
                  });
                });
              }}
            />
          </StickyDiv>
        </Grid>
      </Grid>
    </>
  );
};

DocumentVisualizationDeathComponent.displayName = 'Document Death form component';

const DocumentVisualizationDeathForm = memo(DocumentVisualizationDeathComponent);

export default DocumentVisualizationDeathForm;
